import type { NodeCategory, NodeRecord } from './nodeForm';

export interface NodeTierOption {
  id: number;
  name: string;
  brand: string;
}

export interface NodeListQuery {
  scope: string;
  page: number;
  pageSize: number;
  keyword: string;
  category: NodeCategory | 'all';
  tierId?: number;
}

export interface NodeListSnapshot {
  query: NodeListQuery;
  list: NodeRecord[];
  total: number;
  tiers: NodeTierOption[];
  tiersReady: boolean;
  loading: boolean;
  error: boolean;
  notice: string | null;
}

export const DELETED_TIER_NOTICE =
  '原選取等級已不存在，已切換為全部等級';

export function nodeListUrl(query: NodeListQuery): string {
  const params = new URLSearchParams({
    brand: query.scope,
    page: String(query.page),
    pageSize: String(query.pageSize),
    sortBy: 'region',
    sortOrder: 'asc',
  });
  if (query.keyword) params.set('keyword', query.keyword);
  if (query.category !== 'all') params.set('nodeCategory', query.category);
  if (query.scope !== 'all' && query.tierId !== undefined) {
    params.set('tierId', String(query.tierId));
  }
  return `/api/admin/nodes?${params}`;
}

class NodeListRequestError extends Error {
  constructor(readonly errorCode?: string, readonly status?: number) {
    super('資料載入失敗');
  }
}

type Fetcher = (url: string, options?: RequestInit) => Promise<Response>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function responseData(response: Response): Promise<unknown> {
  const body: unknown = await response.json();
  if (!response.ok || !isRecord(body) || body.code !== 0) {
    const data = isRecord(body) && isRecord(body.data) ? body.data : null;
    throw new NodeListRequestError(
      typeof data?.errorCode === 'string' ? data.errorCode : undefined,
      response.status,
    );
  }
  return body.data;
}

function tierOptions(data: unknown, scope: string): NodeTierOption[] {
  if (!Array.isArray(data)) throw new NodeListRequestError();
  const ids = new Set<number>();
  return data.map(value => {
    if (
      !isRecord(value) || !Number.isSafeInteger(value.id) ||
      (value.id as number) <= 0 || typeof value.name !== 'string' ||
      value.brand !== scope || ids.has(value.id as number)
    ) throw new NodeListRequestError();
    ids.add(value.id as number);
    return { id: value.id as number, name: value.name, brand: scope };
  });
}

function nodePage(data: unknown, pageSize: number) {
  if (
    !isRecord(data) || !Array.isArray(data.list) ||
    !Number.isSafeInteger(data.total) || (data.total as number) < 0 ||
    data.list.length > pageSize || data.list.length > (data.total as number)
  ) throw new NodeListRequestError();
  const ids = new Set<number>();
  for (const value of data.list) {
    if (
      !isRecord(value) || !Number.isSafeInteger(value.id) ||
      (value.id as number) <= 0 || ids.has(value.id as number) ||
      typeof value.name !== 'string' || typeof value.region !== 'string'
    ) throw new NodeListRequestError();
    ids.add(value.id as number);
  }
  return { list: data.list as NodeRecord[], total: data.total as number };
}

/** Page-local data source. Generation checks remain authoritative if abort is ignored. */
export class NodeListSource {
  private listeners = new Set<() => void>();
  private generation = 0;
  private controller?: AbortController;
  private active = false;
  private snapshot: NodeListSnapshot;

  constructor(private readonly fetcher: Fetcher, scope: string) {
    this.snapshot = {
      query: { scope, page: 1, pageSize: 50, keyword: '', category: 'all' },
      list: [], total: 0, tiers: [], tiersReady: false,
      loading: true, error: false, notice: null,
    };
  }

  getSnapshot = () => this.snapshot;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(patch: Partial<NodeListSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  start(scope: string) {
    this.active = true;
    if (this.snapshot.query.scope !== scope) this.setQuery({ scope });
    else void this.load(true);
  }

  stop() {
    this.active = false;
    this.generation++;
    this.controller?.abort();
  }

  setQuery(change: Partial<NodeListQuery>) {
    const previous = this.snapshot.query;
    const scopeChanged = change.scope !== undefined && change.scope !== previous.scope;
    const query = { ...previous, ...change };
    if (scopeChanged) {
      query.tierId = undefined;
      query.page = 1;
    }
    if (query.scope === 'all') query.tierId = undefined;
    this.publish({
      query, list: [], total: 0, loading: true, error: false,
      notice: scopeChanged || 'tierId' in change ? null : this.snapshot.notice,
      ...(scopeChanged ? { tiers: [], tiersReady: false } : {}),
    });
    void this.load(scopeChanged);
  }

  /** User refresh includes options; action/poll refresh only updates node results. */
  refresh = (includeTiers = false) => this.load(includeTiers);

  private async load(refreshTiers: boolean) {
    if (!this.active) return;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const generation = ++this.generation;
    const current = () => this.active && this.generation === generation && !controller.signal.aborted;
    let query = this.snapshot.query;
    const knownTier = query.tierId !== undefined &&
      this.snapshot.tiers.some(tier => tier.id === query.tierId && tier.brand === query.scope);
    const needsTiers = query.scope !== 'all' && (refreshTiers || !this.snapshot.tiersReady);
    this.publish({ list: [], total: 0, loading: true, error: false });

    const readTiers = async () => {
      const response = await this.fetcher(
        `/api/admin/tiers?${new URLSearchParams({ brand: query.scope })}`,
        { signal: controller.signal },
      );
      if (!current()) return null;
      const data = await responseData(response);
      if (!current()) return null;
      return tierOptions(data, query.scope);
    };
    const readNodes = async () => {
      const response = await this.fetcher(nodeListUrl(query), { signal: controller.signal });
      if (!current()) return null;
      const data = await responseData(response);
      if (!current()) return null;
      return nodePage(data, query.pageSize);
    };
    const acceptTiers = (tiers: NodeTierOption[]) => {
      if (!current()) return false;
      this.publish({ tiers, tiersReady: true });
      if (query.tierId === undefined || tiers.some(tier => tier.id === query.tierId)) return true;
      if (!knownTier) throw new NodeListRequestError();
      query = { ...query, tierId: undefined, page: 1 };
      this.publish({ query, notice: DELETED_TIER_NOTICE });
      return true;
    };

    try {
      let page;
      if (needsTiers && query.tierId === undefined) {
        // Independent reads in the all-tiers view; neither source may fail open.
        const [tiers, result] = await Promise.all([readTiers(), readNodes()]);
        if (!current() || !tiers || !result) return;
        acceptTiers(tiers);
        page = result;
      } else {
        if (needsTiers) {
          const tiers = await readTiers();
          if (!current() || !tiers) return;
          acceptTiers(tiers);
        }
        if (query.scope === 'all') this.publish({ tiers: [], tiersReady: true });
        try {
          page = await readNodes();
        } catch (error) {
          if (!current()) return;
          if (
            !(error instanceof NodeListRequestError) ||
            error.status !== 404 || error.errorCode !== 'NODE_FILTER_TIER_NOT_FOUND' || !knownTier ||
            query.tierId === undefined
          ) throw error;
          const tiers = await readTiers();
          if (!current() || !tiers) return;
          if (tiers.some(tier => tier.id === query.tierId)) throw error;
          acceptTiers(tiers);
          page = await readNodes();
        }
      }
      if (!current() || !page) return;
      this.publish({ ...page, loading: false, error: false });
    } catch {
      if (!current()) return;
      this.publish({
        list: [], total: 0, loading: false, error: true,
        ...(needsTiers ? { tiersReady: false } : {}),
      });
    }
  }
}
